import styled from '@emotion/styled/macro';
import { Variables } from '../variables';

const V = Variables.ThemeLight;

/**
 * Layout primitives for an entity detail panel — eyebrow, sections, label/value rows.
 *
 * These were written for the payer drawer and are entirely generic: nothing here knows what a
 * payer is. `payer-drawer.styles.ts` now re-exports them rather than declaring its own, so the
 * drawer and the provider dialog cannot drift into two different section paddings and two
 * different type scales — which is the exact failure the drawer's own header describes having
 * already happened once, when it was two files.
 *
 * Colour is tokens only (NS-2), and the pairings are the semantic ones: `TextStrong` for a
 * value, `TextMild` for a label or a secondary line. No `*Mild*`-on-`*Medium*` pairing appears
 * here — both are surface ramps and together they give roughly 1.2:1.
 */

/** Small uppercase kicker above a title — `RENDERING PROVIDER`. */
export const Eyebrow = styled.span`
    font: 600 10px/1.4 var(--font-sans, inherit);
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

export const Title = styled.h2`
    flex: 1;
    margin: 0;
    font: 600 17px/1.3 var(--font-sans, inherit);
    color: ${V.TextStrong};
`;

export const Section = styled.section`
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
`;

export const SectionHead = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

export const SectionTitle = styled.span`
    font: 600 10px/1.4 var(--font-sans, inherit);
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: ${V.TextMild};
`;

export const SectionMeta = styled.span`
    margin-left: auto;
    font-size: 11px;
    white-space: nowrap;
    color: ${V.TextMild};
`;

export const Card = styled.div`
    padding: 11px 13px;
    border-radius: 8px;
    border: 1px solid ${V.Border1};
    background: ${V.Background3};

    & + & {
        margin-top: 6px;
    }
`;

/**
 * `flex-start`, not `center`.
 *
 * A card's text block runs to three lines — label, destination, detail — and centring the icon
 * against it floats the icon into the middle of the paragraph instead of beside its first line.
 */
export const Row = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
`;

export const Text = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
`;

export const Strong = styled.span`
    font-size: 13px;
    color: ${V.TextStrong};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Muted = styled.span`
    font-size: 11px;
    color: ${V.TextMild};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export const Empty = styled.div`
    padding: 11px 13px;
    border-radius: 8px;
    border: 1px dashed ${V.Border1};
    background: ${V.Background3};
    font-size: 12px;
    color: ${V.TextMild};
`;

/** A label/value pair. The label is fixed-width so a column of them aligns. */
export const Field = styled.div`
    display: flex;
    align-items: baseline;
    gap: 12px;
    min-width: 0;
    padding: 5px 0;

    & + & {
        border-top: 1px solid ${V.Border1};
    }
`;

export const FieldLabel = styled.span`
    flex: 0 0 132px;
    font-size: 12px;
    color: ${V.TextMild};
`;

/**
 * `TextStrong`, and `break-word` rather than ellipsis.
 *
 * A taxonomy code or a truncated NPI is unreadable half-shown, and an identifier that a person
 * is going to compare against a payer portal has to render in full.
 */
export const FieldValue = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: ${V.TextStrong};
    word-break: break-word;
`;

/** A value we do not hold. Muted so it reads as absent rather than as content. */
export const FieldAbsent = styled.span`
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: ${V.TextXMild};
`;

/** Vertical stack for a panel's content — used as the body of a dialog or a drawer. */
export const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
    min-width: 0;
    /*
     * Full width plus border-box, because the panel renders inside a MUI dialog whose content
     * box it must not exceed. Without both, a full-width child carrying padding and a border
     * (the Empty note used for an unbacked section) computes wider than the dialog and its
     * text is clipped mid-sentence at the right edge. Invisible in Storybook, which has no
     * shell to overflow.
     *
     * No backticks in here: this is a styled-component template literal, and one would
     * terminate the string.
     */
    width: 100%;
    box-sizing: border-box;
    padding: 4px 2px 2px;

    * {
        box-sizing: border-box;
    }
`;
